import { Link, useNavigate } from 'react-router-dom';
import { themeClasses, Button, useTheme } from '@member-portal/ui';
import { useAuth } from '@/contexts/AuthContext';

const footerLinks = {
  main: [
    { name: 'Sign In', href: '/login', external: false },
    { name: 'About Us', href: '/about', external: false },
    { name: 'Press', href: '/press', external: false },
    { name: 'Contact Us', href: '/contact', external: false },
    { name: 'Branding Kit', href: 'https://thefourhundred.sharepoint.com/:f:/s/FourHundredbyDesign/IgApa38ab7qjSLk2CAe25zJlAc3rW7ylJQdFx01E_FGgZVw?e=WEjoH9', external: true },
    { name: 'Advisor Sign Up', href: '/signup', external: false },
    { name: 'FAQ', href: '/faq', external: false },
  ],
  legal: [
    { name: 'Terms and Conditions', href: '/terms' },
    { name: 'Privacy Policy', href: '/privacy' },
  ],
};

function Footer() {
  const navigate = useNavigate();
  const { isLightTheme } = useTheme();
  const { isAuthenticated } = useAuth();

  const mainLinks = footerLinks.main.filter((link) => {
    if (isAuthenticated && (link.href === '/signup' || link.href === '/login')) {
      return false;
    }
    return true;
  });

  return (
    <footer className="bg-theme-bg border-t border-theme-border">
      <div className={`${themeClasses.container} py-16`}>
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12 mb-12">
          {/* Brand & Social */}
          <div>
            <Link to="/" className="inline-block mb-6">
              <img
                src={isLightTheme ? '/images/logos/fhxd-logo-black.png' : '/images/logos/fhxd-logo-white.png'}
                alt="Four Hundred by Design"
                className="h-10 w-auto"
              />
            </Link>
            {/* Social Links */}
            <div className="flex items-center space-x-4 mb-6">
              <a
                href="https://www.instagram.com/fourhundredbydesign/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-theme-text-muted hover:text-theme-text transition-colors duration-300"
                aria-label="Instagram"
              >
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                </svg>
              </a>
              <a
                href="https://www.facebook.com/groups/fhxdadvisorforum"
                target="_blank"
                rel="noopener noreferrer"
                className="text-theme-text-muted hover:text-theme-text transition-colors duration-300"
                aria-label="Facebook"
              >
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                </svg>
              </a>
              <a
                href="https://www.linkedin.com/company/four-hundred-by-design/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-theme-text-muted hover:text-theme-text transition-colors duration-300"
                aria-label="LinkedIn"
              >
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                </svg>
              </a>
            </div>
            {/* Partner CTA - hide when logged in */}
            {!isAuthenticated && (
              <div>
                <p className={`${themeClasses.text.muted} text-sm mb-2`}>Looking to partner?</p>
                <Link
                  to="/for-partners"
                  className={`${themeClasses.text.body} text-sm text-theme-text hover:text-theme-text-muted transition-colors duration-300 underline`}
                >
                  Learn more →
                </Link>
              </div>
            )}
          </div>

          {/* Navigation Links */}
          <div>
            <h4 className={`${themeClasses.text.caption} mb-4`}>
              Quick Links
            </h4>
            <nav className="grid grid-cols-2 gap-3">
              {mainLinks.map((link) => (
                link.external ? (
                  <a
                    key={link.name}
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`${themeClasses.text.muted} text-sm hover:text-theme-text transition-colors duration-300`}
                  >
                    {link.name}
                  </a>
                ) : (
                  <Link
                    key={link.name}
                    to={link.href}
                    className={`${themeClasses.text.muted} text-sm hover:text-theme-text transition-colors duration-300`}
                  >
                    {link.name}
                  </Link>
                )
              ))}
            </nav>
          </div>

          {/* CTA — hide when logged in */}
          {!isAuthenticated && (
            <div>
              <h4 className={`${themeClasses.text.caption} mb-4`}>
                Get Started
              </h4>
              <p className={`${themeClasses.text.muted} text-sm mb-6`}>
                Discover a world-class partner collection of hotels, resorts, villas, private residences, specialty cruise lines and providers of luxury goods and services, and begin earning more faster. Registration is complimentary with no obligation.
              </p>
              <Button variant="primary" onClick={() => navigate('/signup')}>
                Advisor Sign Up
              </Button>
            </div>
          )}
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-theme-border">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            {/* Copyright */}
            <p className={`${themeClasses.text.caption} text-center md:text-left`}>
              &copy; {new Date().getFullYear()} Four Hundred by Design, LLC. All Rights Reserved.
              <br />
              Copying is strictly prohibited.
            </p>

            {/* Legal Links */}
            <div className="flex items-center space-x-4">
              {footerLinks.legal.map((link, index) => (
                <span key={link.name} className="flex items-center">
                  <Link
                    to={link.href}
                    className={`${themeClasses.text.caption} hover:text-theme-text transition-colors duration-300`}
                  >
                    {link.name}
                  </Link>
                  {index < footerLinks.legal.length - 1 && (
                    <span className="ml-4 text-theme-border">|</span>
                  )}
                </span>
              ))}
            </div>
          </div>

          {/* Regulatory Info */}
          <p className={`${themeClasses.text.caption} text-center mt-6`}>
            Florida Seller of Travel Reg. No. ST45437 | California Seller of Travel Reg. No. 2162903-50
          </p>
        </div>
      </div>
    </footer>
  );
}

export default Footer;
